import { getImagePath } from '@/utils/images';
import WebinarHtml from './WebinarHtml';
import { Container } from './WebinarShared';
import { CalendarIcon, VideoIcon } from './WebinarIcons';

const PILL_ICONS = { calendar: CalendarIcon, video: VideoIcon };

const HeroButton = ({ button }) => {
  const isSecondary = button.variant && button.variant !== 'default';
  const base = 'inline-flex items-center justify-center gap-2 rounded-full text-base transition-colors';
  const style = isSecondary
    ? 'border border-white/35 px-[27px] py-3.5 text-white hover:border-brand-400 hover:text-brand-400'
    : 'bg-brand-400 px-7 py-[15px] text-white hover:bg-brand-500';
  return (
    <a href={button.url || '#'} className={`${base} ${style}`}>
      {button.label}
    </a>
  );
};

const WebinarHero = ({ heroLogos, eyebrow, heading, lead, sub, metaPills, buttons, scheduleTitle, scheduleRows }) => {
  return (
    <section className="relative overflow-hidden bg-black pt-20 pb-[88px] text-white">
      {/* decorative purple blob */}
      <div className="pointer-events-none absolute right-[6%] -bottom-[180px] z-0 h-[480px] w-[480px] rounded-full bg-[#9f5cf0] opacity-45 blur-[110px] max-[960px]:-right-[100px]" />

      <Container className="relative z-[1]">
        <div className="grid items-end gap-10 min-[960px]:grid-cols-[1.1fr_.9fr]">
          <div>
            {heroLogos?.url && (
              <img
                src={getImagePath(heroLogos)}
                alt={heroLogos.alternativeText || 'Leadhub × Webscale'}
                className="mb-9 h-auto max-h-[100px] w-auto max-w-full object-contain"
              />
            )}
            {eyebrow && (
              <div className="mb-4 inline-flex items-center gap-2 text-sm font-semibold tracking-[0.08em] text-brand-400 uppercase">
                {eyebrow}
              </div>
            )}
            <WebinarHtml
              as="h1"
              html={heading}
              className="mt-1 font-poly text-[40px] leading-[1.2] font-light tracking-[-1.2px] text-white md:text-[60px] [&_em]:whitespace-nowrap [&_em]:text-brand-400 [&_em]:not-italic"
            />
            {lead && <p className="mt-[18px] max-w-[520px] text-xl leading-[1.4] font-medium text-white/85">{lead}</p>}
            {sub && <p className="mt-3 max-w-[520px] text-base leading-[1.6] text-white/65">{sub}</p>}

            {metaPills?.length > 0 && (
              <div className="mt-7 flex flex-wrap gap-3">
                {metaPills.map((pill) => {
                  const Icon = PILL_ICONS[pill.icon] || CalendarIcon;
                  return (
                    <div
                      key={pill.id}
                      className="flex items-center gap-2 rounded-full bg-white/[0.06] py-[9px] pr-4 pl-3 text-sm text-white"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-400 text-white">
                        <Icon width={14} height={14} />
                      </span>
                      {pill.label}
                    </div>
                  );
                })}
              </div>
            )}

            {buttons?.length > 0 && (
              <div className="mt-8 flex flex-wrap items-center gap-4">
                {buttons.map((button) => (
                  <HeroButton key={button.id} button={button} />
                ))}
              </div>
            )}
          </div>

          {scheduleRows?.length > 0 && (
            <div className="rounded-2xl border border-[#404040] p-7">
              {scheduleTitle && (
                <h4 className="mb-[18px] font-sans text-sm font-semibold tracking-[0.06em] text-white/50 uppercase">
                  {scheduleTitle}
                </h4>
              )}
              {scheduleRows.map((row, i) => (
                <div
                  key={row.id}
                  className={`flex items-start gap-5 py-3 ${
                    row.highlight
                      ? 'mt-1 border-t border-dashed border-[#404040]'
                      : i === 0
                        ? ''
                        : 'border-t border-[#404040]'
                  }`}
                >
                  <div className="w-10 shrink-0 font-poly text-[22px] text-brand-400">{row.number}</div>
                  <div>
                    {row.date && <div className="mb-0.5 text-[13px] text-white/55">{row.date}</div>}
                    {row.title && <div className="text-[15px] font-semibold text-white">{row.title}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

export default WebinarHero;
